// SuggestionCard — one row in the mobile Inbox "Captures awaiting confirmation" tier (doc §4.5).
//
// Renders a single machine-proposed write (a `research_capture_suggestions` row, status='proposed')
// with Accept / Reject buttons that call the live `research_capture_suggestion_decide` RPC. Adapted
// from ApprovalCard.tsx: same optimistic-concurrency + live-only-write posture, retargeted at the
// suggestions decide contract.
//
// The RPC is an OPTIMISTIC-CONCURRENCY write: `_expected_updated_at` is the row's current `updated_at`
// token. The card OWNS its token via local state so a `stale` loss can re-seed it from the RPC's
// returned `current.updated_at` and an immediate retry can then succeed without a full refetch. The
// parent passes the initial token + `onResolved(decision)` to drop the row when the write lands
// (accept/reject/already_applied) or when the row is gone (not_found).
//
// Offline: suggestion decisions are LIVE writes — NEVER queued (mirrors ApprovalCard + the mobile hard
// line). When `online` is false the buttons are disabled with a clear "needs a connection" hint.
//
// `42501` (author gate failed server-side) → a "No access" hint, row stays interactive (matches §4.5).
// The mobile card intentionally has NO amend-then-accept (no `_overrides`): rich editing lives on the
// hub page; here the analyst accepts the proposal as-is or rejects it. Edge kinds whose endpoints are
// unresolved (accept would RAISE server-side) get a "resolve on desktop first" hint and no Accept.

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Check, X, WifiOff, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  suggestionType as suggestionTypeLabel,
  confidenceTier,
  humanise,
} from '@/components/research-hub/labels';

export type SuggestionDecision = 'accept' | 'reject';

export interface SuggestionCardProps {
  /** research_capture_suggestions.id */
  id: string;
  /** research_suggestion_type enum (kg_node | kg_edge | thesis_attachment | macro_prediction | drift_flag | table_data). */
  suggestionType: string;
  /** Short human label for the row (may be null → derived from the payload). */
  title: string | null;
  /** One-line model justification. */
  rationale: string | null;
  /** 0..1 confidence (shown as a labelled tier, never a bare decimal). */
  confidence: number | null;
  /** Per-type payload — used to summarise + to detect unresolved kg_edge endpoints. */
  payload: Record<string, unknown>;
  /** The row's CURRENT updated_at — the optimistic-concurrency token. */
  updatedAt: string;
  /** Synchronous reachability — when false the action buttons are disabled. */
  online: boolean;
  /**
   * Called once the row reaches a terminal outcome (applied, already-applied, or gone) so the parent
   * can drop it. Not called on `stale` (the card re-seeds its token and stays put for a retry) nor on
   * transient/permission errors (the card surfaces a toast and stays interactive).
   */
  onResolved: (decision: SuggestionDecision | 'gone') => void;
}

// research_capture_suggestion_decide return envelope (types are stale for this NEW RPC → local).
interface DecideResult {
  ok?: boolean;
  reason?: 'accept' | 'reject' | 'already_applied' | 'stale' | 'not_found' | string;
  current?: { updated_at?: string | null } & Record<string, unknown>;
}

function relativeOrNull(iso: string): string | null {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// A one-line human summary of a suggestion payload for the row when no title is set. Mirrors
// ResearchSuggestions.summariseSuggestion but trimmed for the mobile card.
function summarise(type: string, payload: Record<string, any>): string {
  const p = payload ?? {};
  switch (type) {
    case 'kg_node':
      return `Add “${p.label ?? 'an entity'}”`;
    case 'kg_edge':
      return `${p?.from?.label ?? '?'} → ${p?.to?.label ?? '?'}`;
    case 'thesis_attachment':
      return `Attach evidence to ${humanise(p.target_kind) || 'a target'}`;
    case 'macro_prediction':
      return `${p.organisation ?? 'A house'} forecast for ${p.fred_series_id ?? 'a series'}`;
    case 'drift_flag':
      return p.summary ?? 'New evidence contradicts a held claim';
    case 'table_data':
      return `Table: ${p.table_title ?? 'extracted data'}`;
    default:
      return 'A proposed change';
  }
}

// A kg_edge accept RAISEs server-side unless BOTH endpoints resolve to a node_id. The mobile card
// can't resolve nodes (no inline resolver — that's a desktop affordance), so it hides Accept and
// nudges the analyst to the hub. Non-edge kinds are always acceptable here.
function needsDesktopResolve(type: string, payload: Record<string, any>): boolean {
  if (type !== 'kg_edge') return false;
  const from = payload?.from ?? {};
  const to = payload?.to ?? {};
  return !from.node_id || !to.node_id;
}

export function SuggestionCard({
  id,
  suggestionType,
  title,
  rationale,
  confidence,
  payload,
  updatedAt,
  online,
  onResolved,
}: SuggestionCardProps) {
  const { toast } = useToast();
  // The concurrency token is owned here so a `stale` loss can re-seed it from the RPC's `current`.
  const [token, setToken] = useState(updatedAt);
  // Per-row busy state — guards against double-submit while a decision is in flight.
  const [busy, setBusy] = useState<SuggestionDecision | null>(null);
  // 42501 renders a "no access" hint inline and keeps the row (matches the hub page + §4.5).
  const [denied, setDenied] = useState(false);

  const conf = confidenceTier(confidence);
  const when = relativeOrNull(token);
  const label = title ?? summarise(suggestionType, payload ?? {});
  const resolveOnDesktop = needsDesktopResolve(suggestionType, payload ?? {});

  async function decide(decision: SuggestionDecision) {
    if (busy || !online) return;
    setBusy(decision);
    try {
      const { data, error } = await (supabase.rpc as any)('research_capture_suggestion_decide', {
        _suggestion_id: id,
        _expected_updated_at: token,
        _decision: decision,
        _reason: null,
        // No amend-then-accept on mobile — accept the proposal as-is.
        _overrides: {},
      });

      if (error) {
        // 42501 == author gate failed server-side (the user can SEE the row via can_read_research()
        // but lacks the author capability). Surface inline + toast; leave the row interactive.
        const is42501 = (error as { code?: string }).code === '42501';
        if (is42501) setDenied(true);
        toast({
          variant: 'destructive',
          title: is42501 ? 'No access' : 'Couldn’t apply',
          description: is42501
            ? 'You don’t have permission to action this suggestion.'
            : 'Something went wrong — please try again.',
        });
        return;
      }

      const res = (data ?? {}) as DecideResult;
      const reason = res.reason;

      // `reason` is authoritative — the RPC always returns one. Do NOT short-circuit on `res.ok`
      // (a `stale` carries ok:false; branching on ok could wrongly drop the row instead of re-seeding).
      if (reason === 'accept' || reason === 'reject' || reason === 'already_applied') {
        toast({
          title:
            reason === 'already_applied'
              ? 'Already actioned'
              : decision === 'accept'
                ? 'Accepted'
                : 'Rejected',
          description:
            reason === 'already_applied'
              ? 'This was already decided elsewhere.'
              : decision === 'accept'
                ? `“${label}” written to the graph.`
                : `“${label}” rejected.`,
        });
        onResolved(decision);
        return;
      }

      // Optimistic-concurrency loss: re-seed the token from the fresh row so an immediate retry can
      // succeed; keep the card in place.
      if (reason === 'stale') {
        const fresh = res.current?.updated_at;
        if (typeof fresh === 'string' && fresh) setToken(fresh);
        toast({
          variant: 'destructive',
          title: 'Changed elsewhere',
          description: 'This suggestion was updated — refreshed, please try again.',
        });
        return;
      }

      // Row no longer exists → drop it.
      if (reason === 'not_found') {
        toast({ title: 'No longer available', description: 'This suggestion has gone.' });
        onResolved('gone');
        return;
      }

      // Unknown reason (e.g. a server RAISE surfaced as ok:false without a mapped reason, such as an
      // unresolved-endpoint kg_edge) — be conservative: surface it, keep the row.
      toast({
        variant: 'destructive',
        title: 'Couldn’t apply',
        description:
          typeof reason === 'string' && reason
            ? humanise(reason)
            : 'Unexpected response — please try again.',
      });
    } catch {
      // Network/transient failure (e.g. dropped mid-write). Keep the row; let the user retry.
      toast({
        variant: 'destructive',
        title: 'Couldn’t reach the server',
        description: 'Check your connection and try again.',
      });
    } finally {
      setBusy(null);
    }
  }

  const actionsDisabled = !online || busy != null || denied;

  return (
    <li className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold leading-snug">{label}</p>
          <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
            {suggestionTypeLabel(suggestionType)} · {conf.label}
            {when ? ` · ${when}` : ''}
          </p>
          {rationale && (
            <p className="mt-2 text-sm italic text-muted-foreground">“{rationale}”</p>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {resolveOnDesktop ? (
          <p className="flex-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">
            Resolve entities on desktop before accepting
          </p>
        ) : (
          <button
            type="button"
            onClick={() => decide('accept')}
            disabled={actionsDisabled}
            aria-label={`Accept ${label}`}
            className={cn(
              'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
              'border-primary/30 bg-primary/10 text-primary active:bg-primary/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {busy === 'accept' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="h-4 w-4" aria-hidden="true" />
            )}
            Accept
          </button>
        )}
        <button
          type="button"
          onClick={() => decide('reject')}
          disabled={actionsDisabled}
          aria-label={`Reject ${label}`}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            resolveOnDesktop ? 'flex-1' : 'flex-1',
            'border-border bg-muted/40 text-foreground active:bg-muted',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {busy === 'reject' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <X className="h-4 w-4" aria-hidden="true" />
          )}
          Reject
        </button>
      </div>

      {denied && (
        <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-amber-600">
          You can view this but not action it — needs the author capability.
        </p>
      )}

      {!online && !denied && (
        <p className="mt-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">
          <WifiOff className="h-3 w-3" aria-hidden="true" />
          Needs a connection to action
        </p>
      )}
    </li>
  );
}

export default SuggestionCard;
